import styled from '@emotion/styled'
import { Dimension } from '@pubstack/common/src/analytics/dimension'
import { AnalyticsQueryDashboard, BidMetricsByTime } from '@pubstack/common/src/analytics/query'
import { useAtom } from 'jotai'
import { DateTime } from 'luxon'
import { FunctionComponent, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAnalyticsQueryTanstack } from '~/api/analytics/analytics-query.api'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { LazyVisible } from '~/components/LazyVisible'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { useBreakdownTabs } from '~/hooks/useBreakdownTabs'
import { useScopeCurrency } from '~/hooks/useScopeCurrency.hooks'
import { AnalyticsPage } from '~/modules/analytics/AnalyticsPage'
import { useBuildContextWithFilterEnforced } from '~/modules/analytics/useContext'
import { contextState } from '~/state'
import { onBreakdownRowClick } from '~/utils/analytics'
import { BidderBreakdown } from './BidderBreakdown'
import { PureBidderCharts } from './PureBidderCharts'

const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  & > * + * {
    margin-top: 20px;
  }
`

const FILTER_DIMENSIONS: Dimension[] = ['tagId', 'adUnit', 'device', 'country', 'size', 'pubstackRefresh', 'stackIdVersion']
const QUERY_DIMENSIONS: Dimension[] = [...FILTER_DIMENSIONS, 'bidder']
const DASHBOARD: AnalyticsQueryDashboard = { name: 'bidder', filterType: 'auctions' }
const MAX_NUMBER_OF_DAY_IN_TIMERANGE_SELECTED = 31

export const BidderDetailsPage: FunctionComponent = () => {
  const [context, setContext] = useAtom(contextState)
  const currencySymbol = useScopeCurrency()
  const breadcrumbs = useBreadcrumbs()
  const user = useAuthenticatedUser()
  const tabsConfig = useBreakdownTabs(user, ['tagId', 'adUnit', 'device', 'country', 'size'], { stackIdVersion: 'adstack' })
  const [breakdownDimension, setBreakdownDimension] = useState<Dimension>('tagId')
  const search = useSearchParams()
  const searchParams = JSON.parse(atob(search[0].get('mapping') ?? "{bidder: '', bidderName: ''}"))
  const { bidder, bidderName } = searchParams
  const buildContext = useBuildContextWithFilterEnforced('bidder', { label: bidderName, value: bidder })
  const analyticsQueryByTime = useAnalyticsQueryTanstack<BidMetricsByTime>('bid.metrics.by.time', QUERY_DIMENSIONS, context, { bidderName: bidder })

  const onBreakdownTabChange = (dimension: Dimension) => {
    setBreakdownDimension(dimension)
  }

  const onResponsesTabChange = () => {}
  const onCPMTabChange = () => {}

  return (
    <AnalyticsPage
      onRefreshClick={() => {
        analyticsQueryByTime.refetch()
      }}
      filterDimensions={FILTER_DIMENSIONS}
      queryDimensions={QUERY_DIMENSIONS}
      buildContext={buildContext}
      dashboard={DASHBOARD}
      title={breadcrumbs}
      maxNumberOfDayInTimerangeSelected={MAX_NUMBER_OF_DAY_IN_TIMERANGE_SELECTED}
    >
      <PageContent>
        <PureBidderCharts
          data={analyticsQueryByTime.data ?? { epoch: [] }}
          currentEpoch={DateTime.now()}
          onRefreshClick={() => {
            analyticsQueryByTime.refetch()
          }}
          currencySymbol={currencySymbol}
          empty={!analyticsQueryByTime.isLoading && !analyticsQueryByTime?.data?.epoch?.length}
          error={analyticsQueryByTime.isError}
          isLoading={analyticsQueryByTime.isFetching}
          onResponsesTabChange={onResponsesTabChange}
          onCPMTabChange={onCPMTabChange}
          timezone={context.timeRange.tz}
        />
        <LazyVisible minHeight={400}>
          <BidderBreakdown
            context={context}
            dimensions={QUERY_DIMENSIONS}
            dimension={breakdownDimension}
            bidder={bidder}
            currencySymbol={currencySymbol}
            onTabChange={onBreakdownTabChange}
            onRowClick={onBreakdownRowClick(breakdownDimension, setContext)}
            tabsConfig={tabsConfig}
          />
        </LazyVisible>
      </PageContent>
    </AnalyticsPage>
  )
}
